import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as PlusIcon } from '@pkg/assets/icons/v2/plus.svg';
import { YatDashboardMobileSidebar } from '@pkg/components/Dashboard/MobileSidebar';
import { DropdownPanel } from '@pkg/components/DropdownPanel';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { ChipColor } from '@pkg/components/Sections/Profile/Chip';
import { ChipMember } from '@pkg/components/Sections/Profile/Chips/ChipMember';
import { YatSidebarList } from '@pkg/components/YatSidebar/YatSidebarList';
import { useAppDispatch } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { setMobileSidebarIsOpen } from '@app/store/actions/dashboard';
import { useProfileNotificationsStore } from '@app/store/zustand/profile-page/useProfileNotificationsStore';

import './SideBarStyles.scss';
import { type YatPickerDropdownProps } from './types';

import {
    Chevron,
    ChevronWrapper,
    ChipMemberWrapper,
    CreateYatButtonWrapper,
    MobileDropdownPanelTrigger,
    NoYatElement,
    WhatsThis,
    YatPickerLabel,
} from './styles';

export function YatPickerDropdown({ primaryYat, hasYats, isRelative }: YatPickerDropdownProps) {
    const [translate] = useTranslation('groups');
    const { tablet } = useMediaQuery();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);

    const { setOpenPrimaryYatModal, setSelectedYat } = useProfileNotificationsStore();

    const handleClick = () => {
        setIsOpen(!isOpen);
    };

    const handleClickOutside = () => {
        setIsOpen(false);
    };

    const handleMobileTriggerClick = () => {
        dispatch(setMobileSidebarIsOpen(true));
    };

    const handleCreateYatButtonClick = () => {
        navigate('/create', { state: { enableGoBack: true } });
    };

    const triggerElement = primaryYat ? (
        <div style={{ position: 'relative' }}>
            <ChipMemberWrapper>
                <ChipMember yat={primaryYat} color={ChipColor.Transparent} />
            </ChipMemberWrapper>
        </div>
    ) : (
        <NoYatElement>{translate('no-yats')} 🥺</NoYatElement>
    );

    return (
        <div className="YatPickerDropdown" style={{ zIndex: 10 }}>
            {tablet ? (
                <DropdownPanel
                    trigger={triggerElement}
                    open={isOpen}
                    onClick={handleClick}
                    onClickOutside={handleClickOutside}
                    isRelative={isRelative}
                >
                    <div className="YatPickerDropdown-yat-sidebar yat-sidebar--is-expanded yat-sidebar__list">
                        {hasYats && (
                            <>
                                <YatPickerLabel type="label">
                                    {translate('select-your-yat')}
                                    <WhatsThis
                                        onClick={() => {
                                            setSelectedYat(primaryYat);
                                            setOpenPrimaryYatModal(true);
                                        }}
                                    >{`What's this?`}</WhatsThis>
                                </YatPickerLabel>
                                <YatSidebarList
                                    isPrimaryYatPicker={true}
                                    onClick={handleClickOutside}
                                    fixedHeight={360}
                                />
                            </>
                        )}

                        <CreateYatButtonWrapper>
                            <Button
                                size="medium"
                                variant="solid"
                                startIcon={<PlusIcon />}
                                onClick={handleCreateYatButtonClick}
                            >
                                {translate('create-yat')}
                            </Button>
                        </CreateYatButtonWrapper>
                    </div>
                </DropdownPanel>
            ) : (
                <>
                    <MobileDropdownPanelTrigger onClick={handleMobileTriggerClick}>
                        {triggerElement}
                        <ChevronWrapper>
                            <Chevron />
                        </ChevronWrapper>
                    </MobileDropdownPanelTrigger>
                    <YatDashboardMobileSidebar isPrimaryYatPicker={true} />
                </>
            )}
        </div>
    );
}
